/* eslint-disable array-callback-return */
import _ from "lodash";
import moment from "moment";
import { store } from "..";
import { TYPES } from "../data/hospital.data";
import {
	setFacilityPercentage as setFacilityPercentageComparison,
	setFilterTotal as setFilterTotalComparison,
	setIsCensusTotalLocked as setIsCensusTotalLockedComparison,
	setLockTotal as setLockTotalComparison,
	setLockedByFacility as setLockedByFacilityComparison,
	setLockedTotalBy as setLockedTotalByComparison,
} from "../store/reducers/comparisonReducers/hospitalComparison.slice";
import {
	setFacilityPercentage,
	setFilterTotal,
	setIsCensusTotalLocked,
	setLockTotal,
	setLockedByFacility,
	setLockedTotalBy,
} from "../store/reducers/hospital.slice";
import { TOTAL_TYPE, CUSTOM_TAB_TYPE } from "../types/common.type";
import { HOSPITAL_CARDS_TYPE } from "../types/hospital.type";
import { PAGE_TYPE } from "../types/pages.type";
import { dynamicCardFilter, filterCustomPatientData, filterListDataItems, getDynamicPercentageBy, getPercentageByTotal, itemPercentage, processDynamicCard, updateListTotalValue } from "./common";
import { batch } from "react-redux";
import { getCustomTabsCards } from "./custom-tab-filter";
import { ADMISSION_CARDS_LABELS, ADMISSION_CARDS_TYPE } from "../types/admission.type";

const filterData = async (arr, callback) => {
	const fail = Symbol();
	return (await Promise.all(arr.map(async (item) => ((await callback(item)) ? item : fail)))).filter((i) => i !== fail);
};

// Attach level1/level2/level3/level4/level5 totals to list items for tooltip only (non-mutating calculations).
const attachLevelTotals = (list, snapshots, currentLevel, getKey, cardTitles = {}) => {
	if (!Array.isArray(list) || currentLevel < 2 || !snapshots) return list;
	const level1 = Array.isArray(snapshots.level1) ? snapshots.level1 : [];
	const level2 = Array.isArray(snapshots.level2) ? snapshots.level2 : [];
	const level3 = Array.isArray(snapshots.level3) ? snapshots.level3 : [];
	const level4 = Array.isArray(snapshots.level4) ? snapshots.level4 : [];
	const level5 = Array.isArray(snapshots.level5) ? snapshots.level5 : [];
	return list.map((item) => {
		// Preserve existing level totals if they're already set (e.g., from dxData, shiftData)
		if (item.level1Total !== undefined || item.level2Total !== undefined || item.level3Total !== undefined || item.level4Total !== undefined || item.level5Total !== undefined) {
			return {
				...item,
				...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
			};
		}
		const key = getKey(item);
		if (key === undefined || key === null) return item;
		const level1Total = level1.filter((p) => getKey(p) === key).length;
		const level2Total = level2.filter((p) => getKey(p) === key).length;
		const level3Total = level3.filter((p) => getKey(p) === key).length;
		const level4Total = level4.filter((p) => getKey(p) === key).length;
		const level5Total = level5.filter((p) => getKey(p) === key).length;
		return {
			...item,
			...(currentLevel >= 2 && { level1Total }),
			...(currentLevel >= 3 && { level2Total }),
			...(currentLevel >= 4 && { level3Total }),
			...(currentLevel >= 5 && { level4Total }),
			...(currentLevel >= 6 && { level5Total }),
			...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
		};
	});
};

// Helper to attach level totals when result is an array; keeps original data intact.
const maybeAttachTotals = (dataArr, snapshots, currentLevel, keySelector, cardTitles = {}) => {
	if (!Array.isArray(dataArr)) return dataArr;
	return attachLevelTotals(dataArr, snapshots, currentLevel, keySelector, cardTitles);
};

// * added comparison
export async function updateFacilityPercentageTotal(data, forComparison) {
	let facilityWiseTotal = _(data)
		.groupBy("facilityId")
		.sortBy((group) => data.indexOf(group[0]))
		.map((product) => {
			return {
				id: product[0]?.facilityId,
				total: product.length || 0,
			};
		})
		.value();

	if (forComparison) {
		const currentFacilityPercentage = store.getState().hospitalComparison.facilityPercentage;
		if (!_.isEqual(currentFacilityPercentage, facilityWiseTotal)) {
			store.dispatch(setFacilityPercentageComparison(facilityWiseTotal));
		}
	} else {
		const currentFacilityPercentage = store.getState().hospital.facilityPercentage;
		if (!_.isEqual(currentFacilityPercentage, facilityWiseTotal)) {
			store.dispatch(setFacilityPercentage(facilityWiseTotal));
		}
	}
}

// * added comparison
export async function updateFilterListData(cardFilter, transferType, patientList, priorityData = [], forComparison, defaultCardEdited) {
	const {
		dbData,
		lockedTotal,
		lockedTotalBy = null,
	} = forComparison ? store.getState().hospitalComparison : store.getState().hospital;
	
	const { dynamicCards: storeDynamicCards } = store.getState().hospital;
	const dynamicCards = patientList?.dynamicCards ?? storeDynamicCards;
	const customTabs = patientList?.customTabs ?? [];
	const diffDashboardPatients = patientList?.diffDashboardPatients ?? {};
	const customCombineTabData = patientList?.customCombineTabData ?? [];
	let censusAverage;

	if (!forComparison) {
		censusAverage = await getDynamicPercentageBy(dbData);
	} else {
		censusAverage = await getPercentageByTotal(dbData, PAGE_TYPE.HOSPITAL)
	}
	const { list = [], ninetyDaysData, hospitalData: hospitalDBData } = patientList || {};
	let objectCustom = Object();
	let patientData = list;
	let lockedTotalModified = lockedTotal;
	let isComparingAgainstAvgCensus = true;
	// Determine if transferType acts as a realistic filter constraint
	const transferTypeConsumesLevel = transferType && transferType !== TYPES.ALL;
	// Calculate active filters excluding "census" or generic total cards
	const activeFiltersCount = priorityData ? priorityData.filter(e => e.type !== "census" && e.type !== "total" && e.type !== HOSPITAL_CARDS_TYPE.TOTAL && e.type !== TYPES.ALL).length : 0;

	let totalFilterData = {
		originalData: patientData,
		totalType: !forComparison ? (transferTypeConsumesLevel || activeFiltersCount > 0) ? TOTAL_TYPE.FILTER : TOTAL_TYPE.MAIN : TOTAL_TYPE.MAIN,
		totalForPercentage: censusAverage,
		defaultCardEdited: defaultCardEdited,
	};

	if (transferType === TYPES.PLANNED) {
		patientData = _.filter(patientData, {
			transferType: "plannedHospitalTransfer",
		});
	}
	if (transferType === TYPES.UNPLANNED) {
		patientData = _.filter(patientData, {
			transferType: "unplannedHospitalTransfer",
		});
	}

	let filter1 = Object();
	let newSavedFilters = [];
	let patientFilterData = patientData;
	let mainNumPercentage = censusAverage;

	if (transferType) {
		mainNumPercentage = null;
		totalFilterData.totalForPercentage = patientData.length;
		isComparingAgainstAvgCensus = false;
	}


	batch(async () => {
		if (lockedTotalBy) {
			if (
				lockedTotalBy === TYPES.PLANNED ||
				lockedTotalBy === TYPES.UNPLANNED ||
				lockedTotalBy === TYPES.ALL ||
				lockedTotalBy === "census"
			) {

				// if (lockedTotalModified && !transferType && lockedTotalBy !== "census") {
				if (lockedTotalModified && !transferType && lockedTotalBy !== "census") {
					forComparison ? store.dispatch(setLockedTotalByComparison(null)) : store.dispatch(setLockedTotalBy(null));
					forComparison ? store.dispatch(setLockTotalComparison(null)) : store.dispatch(setLockTotal(null));
					lockedTotalModified = null;
				}
				if (!lockedTotalModified) {
					forComparison ? store.dispatch(setLockedTotalByComparison(null)) : store.dispatch(setLockedTotalBy(null));
					forComparison ? store.dispatch(setLockTotalComparison(null)) : store.dispatch(setLockTotal(null));
					lockedTotalModified = null;
				}
			} else {
				const lockedFilterRemoved = _.find(priorityData, { type: lockedTotalBy });
				if (!lockedFilterRemoved) {
					forComparison ? store.dispatch(setLockedTotalByComparison(null)) : store.dispatch(setLockedTotalBy(null));
					forComparison ? store.dispatch(setLockTotalComparison(null)) : store.dispatch(setLockTotal(null));
					lockedTotalModified = null;
				}
			}
		}

		if (lockedTotalModified) {
			totalFilterData.totalForPercentage = lockedTotalModified;
		}

		if (lockedTotalModified && priorityData?.length === 1 && !transferType) {
			totalFilterData.totalForPercentage = lockedTotalModified;
		}

		if (!lockedTotalModified) {
			forComparison
				? store.dispatch(setIsCensusTotalLockedComparison(false))
				: store.dispatch(setIsCensusTotalLocked(false));
		}
		if (priorityData?.length > 0) {
			isComparingAgainstAvgCensus = false;
		}

		if (
			(priorityData?.length > 0 && (!lockedTotalBy || !lockedTotal)) ||
			(priorityData?.length > 0 &&
				lockedTotalBy !== "census" &&
				lockedTotalBy !== TYPES.PLANNED &&
				lockedTotalBy !== TYPES.UNPLANNED &&
				lockedTotalBy !== TYPES.ALL)
		) {
			totalFilterData.totalForPercentage = null;
		}
		const newPayload = [];
		if (forComparison) {
			const currentFacilityPercentage = store.getState().hospitalComparison.facilityPercentage;
			if (!_.isEqual(currentFacilityPercentage, newPayload)) {
				store.dispatch(setFacilityPercentageComparison(newPayload));
			}
		} else {
			const currentFacilityPercentage = store.getState().hospital.facilityPercentage;
			if (!_.isEqual(currentFacilityPercentage, newPayload)) {
				store.dispatch(setFacilityPercentage(newPayload));
			}
		}
	});
	let dynamicCardsObj = {};
	let customTabsObj = {};
	
	// Collect intermediate patient sets before each filter is applied (for tooltip only).
	// This does not change any existing calculations; it's read-only data for UI.
	const intermediateTotals = {};
	const cardTitles = {};
	// If a narrowing transferType is selected, store it as level1 before priorityData loop
	if (transferTypeConsumesLevel) {
		intermediateTotals.level1 = _.cloneDeep(patientFilterData);
		const transferTypeLabel = transferType === TYPES.PLANNED ? "Planned Transfer" : transferType === TYPES.UNPLANNED ? "Unplanned Transfer" : transferType;
		cardTitles.level1 = `${transferTypeLabel} total`;
	}

	// Calculate final level for remaining cards (accounts for transferType and active priority filters)
	const finalLevel = activeFiltersCount + (transferTypeConsumesLevel ? 1 : 0);
	
	if (priorityData?.length > 0) {
		let i = 0;
		let currentValidFilterIndex = 0;
		let previousValidTitle = '';
		// Adjust starting level based on whether a narrowing transferType is selected
		const levelOffset = transferTypeConsumesLevel ? 1 : 0;
		
		for await (const ele of priorityData) {
			i++;
			if (priorityData?.length === i && lockedTotalModified && !lockedTotalBy) {
				forComparison
					? store.dispatch(setLockedTotalByComparison(ele.type))
					: store.dispatch(setLockedTotalBy(ele.type));
			}

			const isTotalLock = ele.type === "census" || ele.type === "total" || ele.type === HOSPITAL_CARDS_TYPE.TOTAL || ele.type === TYPES.ALL;

			if (!isTotalLock) {
				currentValidFilterIndex++;
				
				// Store snapshot BEFORE applying the current filter so downstream cards can show level totals.
				if (currentValidFilterIndex > 1 && currentValidFilterIndex <= 6) {
					const actualLevel = (currentValidFilterIndex - 1) + levelOffset;
					if (actualLevel >= 1 && actualLevel <= 5) {
						intermediateTotals[`level${actualLevel}`] = _.cloneDeep(patientFilterData);
						cardTitles[`level${actualLevel}`] = previousValidTitle ? `${previousValidTitle} total` : '';
					}
				}
				previousValidTitle = ele?.cardTitle || ele?.type || '';
			}
			
			// Calculate actualLevel for use in downstream card processing
			const actualLevel = currentValidFilterIndex + levelOffset;

			if (ele?.question?.isCustom) {
				if (customTabs.length > 0) {
					const customTabsRes = customTabs.filter((x) => x.accessor === ele.type);
					if (customTabsRes.length > 0) {
						// Pass isCheckNewFilters and include current card in newSavedFilters to skip filtering for this card
						// This prevents the selected filter card from updating its own data, similar to default cards
						// For combineTab: Always use mainNumPercentage (original data length before filters) to keep percentage consistent
						// For other custom cards: Use mainNumPercentage when i === 1 (first selected card) to preserve original percentage calculation
						// This matches the pattern used for default cards (e.g., hospitalizations, DCERData, etc.)
						const isCombineTab = ele?.question?.customTab?.type === CUSTOM_TAB_TYPE.combineTab;
						// For combineTab: Always use mainNumPercentage (original data length before filters)
						// If mainNumPercentage is not available, use originalData.length as fallback (originalData is the unfiltered data)
						// This ensures percentage is always calculated based on original data length, not filtered data
						const originalDataLength = totalFilterData?.originalData?.length || patientData.length;
						const totalForPercentage = isCombineTab 
							? (mainNumPercentage || originalDataLength || totalFilterData.totalForPercentage)
							: (mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage);
						
						const isCheckNewFilters = true;
						customTabsObj = await getCustomTabsCards(
							patientFilterData, 
							customTabsRes, 
							{ ...totalFilterData, totalType: TOTAL_TYPE.FILTER, originalData: patientData, totalForPercentage, intermediateTotals, currentLevel: actualLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }, forComparison }, 
							{ ninetyDaysData, dynamicCards, diffDashboardPatients, pageType: PAGE_TYPE.HOSPITAL, forComparison, customCombineTabData, isCheckNewFilters, newSavedFilters: [...newSavedFilters, ele.type] }
						);
					}
				}
				
				objectCustom = { ...objectCustom, ...dynamicCardsObj, ...customTabsObj }
				patientFilterData = filterCustomPatientData(patientFilterData, cardFilter, ele);
				// Add to newSavedFilters after filtering, similar to default cards
				newSavedFilters = [...newSavedFilters, ele.type];
			}
			if (ele.type === HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS) {
				objectCustom.hospitalizations = await hospitalizationData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});

				if (cardFilter.hospitalizations.length === 1) {
					filter1.reHospitalization = hospitalizationsFilter(cardFilter);
					if (!_.isEmpty(filter1)) {
						patientFilterData = _.filter(patientFilterData, filter1);
					}
				}
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.DCER_DATA) {
				objectCustom.DCERData = await dcErData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});

				if (cardFilter.DCERData.length === 1) {
					filter1.wasAdmitted = DCERDataFilter(cardFilter);
					if (!_.isEmpty(filter1)) {
						patientFilterData = _.filter(patientFilterData, filter1);
					}
				}
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.INSURANCE_DATA) {
				objectCustom.insuranceData = await insuranceData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.insuranceData = attachLevelTotals(
					objectCustom.insuranceData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p.insuranceId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				patientFilterData = _.filter(patientFilterData, ({ insuranceId }) => {
					return _.every([_.includes(cardFilter.insuranceData, insuranceId)]);
				});
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.RETURNS_DATA) {
				objectCustom.returnsData = await returnsData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});

				if (cardFilter.returnsData.length === 1) {
					filter1.wasReturned = returnsDataFilter(cardFilter);
					if (!_.isEmpty(filter1)) {
						patientFilterData = _.filter(patientFilterData, filter1);
					}
				}
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA) {
				objectCustom.ninetyDaysData = await ninetyDaysDataList(ninetyDaysData, patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.ninetyDaysData = maybeAttachTotals(
					objectCustom.ninetyDaysData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				if (i === priorityData?.length) {
					updateFacilityPercentageTotal(patientFilterData, forComparison);
				}
				patientFilterData = await ninetyDaysDataFilter(cardFilter.ninetyDaysData, patientFilterData, ninetyDaysData);
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.FLOORS_DATA) {
				objectCustom.floorsData = await floorsData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.floorsData = attachLevelTotals(
					objectCustom.floorsData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p.floorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);

				patientFilterData = await filterData(patientFilterData, ({ floorId }) =>
					_.every([_.includes(cardFilter.floorsData, floorId)])
				);
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.DOCTOR_DATA) {
				objectCustom.doctorData = await doctorData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.doctorData = attachLevelTotals(
					objectCustom.doctorData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p.doctorId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = await filterData(patientFilterData, ({ doctorId }) =>
					_.every([_.includes(cardFilter.doctorData, doctorId)])
				);
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.DAYS_DATA) {
				objectCustom.daysData = await daysData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				patientFilterData = _.filter(patientFilterData, ({ day }) => _.every([_.includes(cardFilter.daysData, day)]));
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.DX_DATA) {
				objectCustom.dxData = await dxData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.dxData = maybeAttachTotals(
					objectCustom.dxData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ dxIds }) => {
					const matchedIds = _.intersection(cardFilter.dxData, dxIds);
					return matchedIds.length > 0 ? true : false;
				});
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.SHIFT_DATA) {
				objectCustom.shiftData = await shiftData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
					cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
				});
				objectCustom.shiftData = maybeAttachTotals(
					objectCustom.shiftData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p._id ?? p.id ?? p.shiftName,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ shiftName }) =>
					_.every([_.includes(cardFilter.shiftData, shiftName)])
				);
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.NURSE_DATA) {
				objectCustom.nurseData = await nurseData(patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.nurseData = attachLevelTotals(
					objectCustom.nurseData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p.nurseId ?? p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = _.filter(patientFilterData, ({ nurseId }) =>
					_.every([_.includes(cardFilter.nurseData, nurseId)])
				);
			}

			if (ele.type === HOSPITAL_CARDS_TYPE.HOSPITAL_DATA) {
				objectCustom.hospitalData = await hospitalDataList(hospitalDBData, patientFilterData, {
					...totalFilterData,
					totalForPercentage: mainNumPercentage && i === 1 ? mainNumPercentage : totalFilterData.totalForPercentage,
					intermediateTotals,
					currentLevel: actualLevel,
				});
				objectCustom.hospitalData = maybeAttachTotals(
					objectCustom.hospitalData,
					{ level1: intermediateTotals.level1, level2: intermediateTotals.level2, level3: intermediateTotals.level3, level4: intermediateTotals.level4, level5: intermediateTotals.level5 },
					actualLevel,
					(p) => p._id ?? p.id,
					{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
				);
				patientFilterData = await hospitalDataFilter(cardFilter.hospitalData, patientFilterData, hospitalDBData);
			}
			newSavedFilters = [...newSavedFilters, ele.type];
			
			if (i === priorityData?.length && ele.type !== HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA) {
				updateFacilityPercentageTotal(patientFilterData, forComparison);
			}
		}

		//UPdate remaining cards data
		const totalPercentageCount = lockedTotalModified ? lockedTotalModified : patientFilterData.length; //transferType ? patientFilterData?.length : censusAverage

		if (forComparison) {
			const currentFilterTotal = store.getState().hospitalComparison.filterTotal;
			if (!_.isEqual(currentFilterTotal ?? 0, totalPercentageCount ?? 0)) {
				store.dispatch(setFilterTotalComparison(totalPercentageCount));
			}
		} else {
			const currentFilterTotal = store.getState().hospital.filterTotal;
			if (!_.isEqual(currentFilterTotal ?? 0, totalPercentageCount ?? 0)) {
				store.dispatch(setFilterTotal(totalPercentageCount));
			}
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS)) {
			objectCustom.hospitalizations = await hospitalizationData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.DCER_DATA)) {
			objectCustom.DCERData = await dcErData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.INSURANCE_DATA)) {
			objectCustom.insuranceData = await insuranceData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.insuranceData = attachLevelTotals(
				objectCustom.insuranceData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p.insuranceId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}

		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.RETURNS_DATA)) {
			objectCustom.returnsData = await returnsData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA)) {
			objectCustom.ninetyDaysData = await ninetyDaysDataList(ninetyDaysData, patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.ninetyDaysData = maybeAttachTotals(
				objectCustom.ninetyDaysData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.FLOORS_DATA)) {
			objectCustom.floorsData = await floorsData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.floorsData = attachLevelTotals(
				objectCustom.floorsData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p.floorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.DOCTOR_DATA)) {
			objectCustom.doctorData = await doctorData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.doctorData = attachLevelTotals(
				objectCustom.doctorData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p.doctorId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.DAYS_DATA)) {
			objectCustom.daysData = await daysData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.DX_DATA)) {
			objectCustom.dxData = await dxData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.dxData = maybeAttachTotals(
				objectCustom.dxData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.SHIFT_DATA)) {
			objectCustom.shiftData = await shiftData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
				cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
			});
			objectCustom.shiftData = maybeAttachTotals(
				objectCustom.shiftData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p._id ?? p.id ?? p.shiftName,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.NURSE_DATA)) {
			objectCustom.nurseData = await nurseData(patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.nurseData = maybeAttachTotals(
				objectCustom.nurseData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p.nurseId ?? p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (!_.includes(newSavedFilters, HOSPITAL_CARDS_TYPE.HOSPITAL_DATA)) {
			objectCustom.hospitalData = await hospitalDataList(hospitalDBData, patientFilterData, {
				...totalFilterData,
				totalType: TOTAL_TYPE.FILTER,
				totalForPercentage: totalPercentageCount,
				intermediateTotals,
				currentLevel: finalLevel,
			});
			objectCustom.hospitalData = maybeAttachTotals(
				objectCustom.hospitalData,
				{ level1: intermediateTotals?.level1, level2: intermediateTotals?.level2, level3: intermediateTotals?.level3, level4: intermediateTotals?.level4, level5: intermediateTotals?.level5 },
				finalLevel,
				(p) => p._id ?? p.id,
				{ level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }
			);
		}
		if (dynamicCards.length > 0) {
			for (const item of dynamicCards) {
				let objectCustomRes = {};
				if (!_.includes(newSavedFilters, item?.accessor)) {
					await processDynamicCard(item, patientFilterData, objectCustomRes, {
						...totalFilterData,
						totalType: TOTAL_TYPE.FILTER,
						totalForPercentage: totalPercentageCount,
						intermediateTotals,
						currentLevel: finalLevel,
						cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 },
						forComparison,
					});
					if (!_.isEmpty(objectCustomRes)) {
						objectCustom[item?.accessor] = objectCustomRes?.[item?.accessor];
					}
				}
			}
		}
		if (customTabs.length > 0) {
			let customTabsObjRes = {};
			const isCheckNewFilters = true;
			customTabsObjRes = await getCustomTabsCards(
				patientFilterData,
				customTabs,
				{ ...totalFilterData, totalType: TOTAL_TYPE.FILTER, totalForPercentage: totalPercentageCount, intermediateTotals, currentLevel: finalLevel, cardTitles: { level1: cardTitles.level1, level2: cardTitles.level2, level3: cardTitles.level3, level4: cardTitles.level4, level5: cardTitles.level5 }, forComparison },
				{ ninetyDaysData, dynamicCards, newSavedFilters, isCheckNewFilters, objectCustom, pageType: PAGE_TYPE.HOSPITAL, diffDashboardPatients, forComparison, customCombineTabData }
			);
			objectCustom = { ...objectCustom, ...customTabsObjRes };
		}
		if (!lockedTotalModified) {
			if (forComparison) {
				const currentLockedByFacility = store.getState().hospitalComparison.lockedByFacility;
				if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
					store.dispatch(setLockedByFacilityComparison(patientFilterData));
				}
			} else {
				const currentLockedByFacility = store.getState().hospital.lockedByFacility;
				if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
					store.dispatch(setLockedByFacility(patientFilterData));
				}
			}
		}
	} else {
		batch(async () => {
			const totalCount = mainNumPercentage ? mainNumPercentage : transferType ? patientFilterData.length : censusAverage;
			if (!transferType && lockedTotalModified) {
				var isCensusTotalLocked = true;
				if (forComparison) {
					const currentIsCensusTotalLocked = store.getState().hospitalComparison.isCensusTotalLocked;
					if (!_.isEqual(currentIsCensusTotalLocked, isCensusTotalLocked)) {
						store.dispatch(setIsCensusTotalLockedComparison(isCensusTotalLocked));
					}
				} else {
					const currentIsCensusTotalLocked = store.getState().hospital.isCensusTotalLocked;
					if (!_.isEqual(currentIsCensusTotalLocked, isCensusTotalLocked)) {
						store.dispatch(setIsCensusTotalLocked(isCensusTotalLocked));
					}
				}
			}
			if (lockedTotalModified && !lockedTotalBy) {
				if (transferType) {
					forComparison
						? store.dispatch(setLockedTotalByComparison(transferType))
						: store.dispatch(setLockedTotalBy(transferType));
				} else {
					forComparison
						? store.dispatch(setLockedTotalByComparison("census"))
						: store.dispatch(setLockedTotalBy("census"));
				}
			}
			if (!lockedTotalModified) {
				if (forComparison) {
					const currentLockedByFacility = store.getState().hospitalComparison.lockedByFacility;
					if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
						store.dispatch(setLockedByFacilityComparison(patientFilterData));
					}
				} else {
					const currentLockedByFacility = store.getState().hospital.lockedByFacility;
					if (!_.isEqual(currentLockedByFacility ?? [], patientFilterData ?? [])) {
						store.dispatch(setLockedByFacility(patientFilterData));
					}
				}
			}
			updateFacilityPercentageTotal(patientFilterData, forComparison);
			store.dispatch(
				forComparison
					? setFilterTotalComparison(lockedTotalModified ? lockedTotalModified : totalCount)
					: setFilterTotal(lockedTotalModified ? lockedTotalModified : totalCount)
			);
		});
		[
			objectCustom.hospitalizations,
			objectCustom.DCERData,
			objectCustom.insuranceData,
			objectCustom.returnsData,
			objectCustom.ninetyDaysData,
			objectCustom.floorsData,
			objectCustom.doctorData,
			objectCustom.daysData,
			objectCustom.dxData,
			objectCustom.shiftData,
			objectCustom.nurseData,
			objectCustom.hospitalData,
		] = await Promise.all([
			hospitalizationData(patientFilterData, totalFilterData),
			dcErData(patientFilterData, totalFilterData),
			insuranceData(patientFilterData, totalFilterData),
			returnsData(patientFilterData, totalFilterData),
			ninetyDaysDataList(ninetyDaysData, patientFilterData, totalFilterData),
			floorsData(patientFilterData, totalFilterData),
			doctorData(patientFilterData, totalFilterData),
			daysData(patientFilterData, totalFilterData),
			dxData(patientFilterData, totalFilterData),
			shiftData(patientFilterData, totalFilterData),
			nurseData(patientFilterData, totalFilterData),
			hospitalDataList(hospitalDBData, patientFilterData, totalFilterData),
		]);
		if (dynamicCards.length > 0) {
			dynamicCardsObj = await dynamicCardFilter(patientFilterData, dynamicCards, { ...totalFilterData, totalType: TOTAL_TYPE.MAIN, intermediateTotals: {}, currentLevel: 0, forComparison });
		}
		if (customTabs.length > 0) {
			customTabsObj = await getCustomTabsCards(patientFilterData, customTabs, { ...totalFilterData, totalType: TOTAL_TYPE.MAIN, intermediateTotals: {}, currentLevel: 0, cardTitles: {}, forComparison }, { ninetyDaysData, dynamicCards, pageType: PAGE_TYPE.HOSPITAL, diffDashboardPatients, forComparison, customCombineTabData });
		}
		objectCustom = { ...objectCustom, ...dynamicCardsObj, ...customTabsObj }
	}
	objectCustom.isComparingAgainstAvgCensus = isComparingAgainstAvgCensus;

	// Page converter uses main dashboard only (same as Hospital page — comparison branch omits this field).
	if (!forComparison) {
		const pageConverterRows = Array.isArray(patientFilterData) ? patientFilterData : [];
		return {
			...objectCustom,
			pageConverterFilteredPatients: _.cloneDeep(pageConverterRows),
		};
	}
	return objectCustom;
}

export async function checkMainArray(data, isFilterSelectedCnt) {
	return isFilterSelectedCnt === 1 && data?.length > 0;
}

export async function selectedFilterCount(oldFilter, type = null) {
	return Object.entries(oldFilter)
		.filter(([key, value]) => key !== type && value?.length > 0)
		.length;
}

export async function checkOnlyOneFilter(oldFilter, filterType = null, currentCardFilter = null, type = null) {
	const oldFiltersData = Object.assign({}, oldFilter);
	delete oldFiltersData["permission"];
	if (type === "DBCheck") {
		let isFilterSelectedCount = 0;
		for (const [key, value] of Object.entries(oldFiltersData)) {
			if (key !== filterType) {
				if (value.length > 0) {
					isFilterSelectedCount++;
				}
			}
		}
		if (isFilterSelectedCount > 0) {
			return false;
		} else {
			return oldFilter[filterType].length > 0 ? true : false;
		}
	} else {
		delete oldFiltersData[type];
		if (currentCardFilter && currentCardFilter.length === 0) {
			let isFilterSelectedCount = 0;
			for (const [key, value] of Object.entries(oldFiltersData)) {
				if (key !== filterType) {
					if (value.length > 0) {
						isFilterSelectedCount++;
					}
				}
			}
			return isFilterSelectedCount > 0 ? false : true;
		} else {
			return false;
		}
	}
}

export function returnsDataFilter(oldFilter) {
	const value = oldFilter?.returnsData?.[0];
	return value === "Returned" ? true : value === "Didn't Return" ? false : undefined;
}

export function hospitalizationsFilter(oldFilter) {
	const value = oldFilter?.hospitalizations?.[0];
	return value === "reHospitalizations" ? true : value === "newHospitalizations" ? false : undefined;
}

export function DCERDataFilter(oldFilter) {
	const value = oldFilter?.DCERData?.[0];
	return value === "DC" ? true : value === "ER" ? false : undefined;
}

export async function hospitalDataFilter(cardFilter, patientData, ninetyDaysData) {
	let ninetyDaysDataIds = [];
	let ninetyDaysDataFilter = _.filter(ninetyDaysData, ({ _id }) => _.every([_.includes(cardFilter, _id)]));
	if (ninetyDaysDataFilter && ninetyDaysDataFilter.length > 0) {
		ninetyDaysDataFilter.map((item) => (ninetyDaysDataIds = [...ninetyDaysDataIds, ...item.ids]));
	}
	patientData = _.filter(patientData, ({ _id }) => _.every([_.includes(ninetyDaysDataIds, _id)]));
	return patientData;
}

export async function ninetyDaysDataFilter(cardFilter, patientData, ninetyDaysData) {
	let ninetyDaysDataIds = [];
	let ninetyDaysDataFilter = _.filter(ninetyDaysData, ({ _id }) => _.every([_.includes(cardFilter, _id)]));
	
	if (ninetyDaysDataFilter && ninetyDaysDataFilter.length > 0) {
		ninetyDaysDataFilter.map((item) => (ninetyDaysDataIds = [...ninetyDaysDataIds, ...item.ids]));
	}
	
	patientData = _.filter(patientData, ({ _id }) => _.every([_.includes(ninetyDaysDataIds, _id)]));

	return patientData;
}

export async function hospitalizationData(data, totalFilter) {
	let newHospitalizationsTotal = 0;
	let reHospitalizationsTotal = 0;
	let newHospitalizationsIds = [];
	let reHospitalizationsIds = [];

	let newHospitalizationsRefIds = [];
	let reHospitalizationsRefIds = [];
	const { originalData = [], totalType = null, totalForPercentage, isOtherDashboard = false, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter;

	const originalDataGroupBy = _.countBy(originalData, "reHospitalization");
	if (data && data.length > 0) {
		await filterData(data, async (ele) => {
			if (ele.reHospitalization) {
				reHospitalizationsTotal++;
				reHospitalizationsIds.push(ele.id);
				if (isOtherDashboard) {
					reHospitalizationsRefIds.push(ele.refPatientId);
				}
			} else {
				newHospitalizationsTotal++;
				newHospitalizationsIds.push(ele.id);
				if (isOtherDashboard) {
					newHospitalizationsRefIds.push(ele.refPatientId);
				}
			}
		});
	}

	// Intermediate level totals (read-only for tooltip)
	let level1NewTotal = 0;
	let level1ReTotal = 0;
	let level2NewTotal = 0;
	let level2ReTotal = 0;
	let level3NewTotal = 0;
	let level3ReTotal = 0;
	let level4NewTotal = 0;
	let level4ReTotal = 0;
	let level5NewTotal = 0;
	let level5ReTotal = 0;

	if (currentLevel >= 2 && intermediateTotals?.level1 && Array.isArray(intermediateTotals.level1)) {
		const level1DataGroupBy = _.countBy(intermediateTotals.level1, "reHospitalization");
		level1NewTotal = level1DataGroupBy?.[false] || 0;
		level1ReTotal = level1DataGroupBy?.[true] || 0;
	}
	if (currentLevel >= 3 && intermediateTotals?.level2 && Array.isArray(intermediateTotals.level2)) {
		const level2DataGroupBy = _.countBy(intermediateTotals.level2, "reHospitalization");
		level2NewTotal = level2DataGroupBy?.[false] || 0;
		level2ReTotal = level2DataGroupBy?.[true] || 0;
	}
	if (currentLevel >= 4 && intermediateTotals?.level3 && Array.isArray(intermediateTotals.level3)) {
		const level3DataGroupBy = _.countBy(intermediateTotals.level3, "reHospitalization");
		level3NewTotal = level3DataGroupBy?.[false] || 0;
		level3ReTotal = level3DataGroupBy?.[true] || 0;
	}
	if (currentLevel >= 5 && intermediateTotals?.level4 && Array.isArray(intermediateTotals.level4)) {
		const level4DataGroupBy = _.countBy(intermediateTotals.level4, "reHospitalization");
		level4NewTotal = level4DataGroupBy?.[false] || 0;
		level4ReTotal = level4DataGroupBy?.[true] || 0;
	}
	if (currentLevel >= 6 && intermediateTotals?.level5 && Array.isArray(intermediateTotals.level5)) {
		const level5DataGroupBy = _.countBy(intermediateTotals.level5, "reHospitalization");
		level5NewTotal = level5DataGroupBy?.[false] || 0;
		level5ReTotal = level5DataGroupBy?.[true] || 0;
	}

	let percentageTotal = totalForPercentage ? totalFilter.totalForPercentage : data?.length;

	const hospitalizationsDBTTotal = [
		{
			_id: "newHospitalizations",
			label: "New Hospitalizations",
			color: "#00BAEB",
			total: newHospitalizationsTotal,
			patientIds: isOtherDashboard ? newHospitalizationsRefIds : newHospitalizationsIds,
			...(isOtherDashboard && { otherDashboardIds: newHospitalizationsIds }),
			originalTotal: originalDataGroupBy?.[false],
			...(currentLevel >= 2 && { level1Total: level1NewTotal }),
			...(currentLevel >= 3 && { level2Total: level2NewTotal }),
			...(currentLevel >= 4 && { level3Total: level3NewTotal }),
			...(currentLevel >= 5 && { level4Total: level4NewTotal }),
			...(currentLevel >= 6 && { level5Total: level5NewTotal }),
			...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
			isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
			ids: newHospitalizationsIds,
			percentage: itemPercentage(newHospitalizationsTotal, percentageTotal, "number"),
			level: totalFilter?.level || 0,
		},
		{
			_id: "reHospitalizations",
			label: "Re-Hospitalizations",
			color: "#5195DD",
			total: reHospitalizationsTotal,
			patientIds: isOtherDashboard ? reHospitalizationsRefIds : reHospitalizationsIds,
			...(isOtherDashboard && { otherDashboardIds: reHospitalizationsIds }),
			ids: reHospitalizationsIds,
			originalTotal: originalDataGroupBy?.[true],
			...(currentLevel >= 2 && { level1Total: level1ReTotal }),
			...(currentLevel >= 3 && { level2Total: level2ReTotal }),
			...(currentLevel >= 4 && { level3Total: level3ReTotal }),
			...(currentLevel >= 5 && { level4Total: level4ReTotal }),
			...(currentLevel >= 6 && { level5Total: level5ReTotal }),
			...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
			...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
			...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
			...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
			...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
			isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
			percentage: itemPercentage(reHospitalizationsTotal, percentageTotal, "number"),
			level: totalFilter?.level || 0,
		},
	];
	return hospitalizationsDBTTotal;
};

export async function dcErData(data, totalFilter) {
	if (!Array.isArray(data) || data.length === 0) {
		console.warn("Invalid or empty data array.");
		return [];
	}

	const { originalData = [], totalType = null, totalForPercentage, isOtherDashboard = false, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter ?? {};

	// Ensure totalForPercentage is valid
	const percentageTotal = totalForPercentage || data.length;

	// Use countBy to group by "wasAdmitted" values
	const DCERResult = _.countBy(data, "wasAdmitted");
	const originalDataGroupBy = _.countBy(originalData, "wasAdmitted");
	const patientIdsByAdmissionStatus = _.groupBy(data, "wasAdmitted");

	// Intermediate level totals for tooltip
	let level1DC = 0;
	let level1ER = 0;
	let level2DC = 0;
	let level2ER = 0;
	let level3DC = 0;
	let level3ER = 0;
	let level4DC = 0;
	let level4ER = 0;
	let level5DC = 0;
	let level5ER = 0;

	if (currentLevel >= 2 && intermediateTotals?.level1 && Array.isArray(intermediateTotals.level1)) {
		const level1DataGroupBy = _.countBy(intermediateTotals.level1, "wasAdmitted");
		level1DC = level1DataGroupBy?.[true] || 0;
		level1ER = level1DataGroupBy?.[false] || 0;
	}
	if (currentLevel >= 3 && intermediateTotals?.level2 && Array.isArray(intermediateTotals.level2)) {
		const level2DataGroupBy = _.countBy(intermediateTotals.level2, "wasAdmitted");
		level2DC = level2DataGroupBy?.[true] || 0;
		level2ER = level2DataGroupBy?.[false] || 0;
	}
	if (currentLevel >= 4 && intermediateTotals?.level3 && Array.isArray(intermediateTotals.level3)) {
		const level3DataGroupBy = _.countBy(intermediateTotals.level3, "wasAdmitted");
		level3DC = level3DataGroupBy?.[true] || 0;
		level3ER = level3DataGroupBy?.[false] || 0;
	}
	if (currentLevel >= 5 && intermediateTotals?.level4 && Array.isArray(intermediateTotals.level4)) {
		const level4DataGroupBy = _.countBy(intermediateTotals.level4, "wasAdmitted");
		level4DC = level4DataGroupBy?.[true] || 0;
		level4ER = level4DataGroupBy?.[false] || 0;
	}
	if (currentLevel >= 6 && intermediateTotals?.level5 && Array.isArray(intermediateTotals.level5)) {
		const level5DataGroupBy = _.countBy(intermediateTotals.level5, "wasAdmitted");
		level5DC = level5DataGroupBy?.[true] || 0;
		level5ER = level5DataGroupBy?.[false] || 0;
	}

	// Common function to generate chart details
	const createChartDetail = (id, label, color, admitted) => ({
		id,
		label,
		_id: id,
		total: DCERResult[admitted] || 0,
		originalTotal: originalDataGroupBy[admitted] || 0,
		...(currentLevel >= 2 && admitted === true && { level1Total: level1DC }),
		...(currentLevel >= 2 && admitted === false && { level1Total: level1ER }),
		...(currentLevel >= 3 && admitted === true && { level2Total: level2DC }),
		...(currentLevel >= 3 && admitted === false && { level2Total: level2ER }),
		...(currentLevel >= 4 && admitted === true && { level3Total: level3DC }),
		...(currentLevel >= 4 && admitted === false && { level3Total: level3ER }),
		...(currentLevel >= 5 && admitted === true && { level4Total: level4DC }),
		...(currentLevel >= 5 && admitted === false && { level4Total: level4ER }),
		...(currentLevel >= 6 && admitted === true && { level5Total: level5DC }),
		...(currentLevel >= 6 && admitted === false && { level5Total: level5ER }),
		...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
		...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
		...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
		...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
		...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
		isTooltip: totalType !== TOTAL_TYPE.MAIN,
		color,
		patientIds: (patientIdsByAdmissionStatus[admitted] || []).map(item => isOtherDashboard ? item.refPatientId : item._id),
		percentage: itemPercentage(DCERResult[admitted], percentageTotal, "number"),
		isDefaultData: !DCERResult[admitted],
		level: totalFilter?.level || 0,
		...isOtherDashboard && { otherDashboardIds: patientIdsByAdmissionStatus[admitted]?.map(item => item._id) }
	});

	// Generate chart details
	return [
		createChartDetail("DC", "DC", "#606BDF", true),
		createChartDetail("ER", "ER", "#87BCFE", false)
	];
}

export async function averageLengthOfStayData(data, totalFilter) {
	if (!Array.isArray(data) || data.length === 0) {
		console.warn("Invalid or empty data array.");
		return [];
	}

	const { originalData = [], totalType = null, totalForPercentage, isOtherDashboard = false } = totalFilter ?? {};

	// Filter data to only include items with valid transferDay values (including 0)
	const validData = data.filter(item =>
		item.transferDay != null &&
		!isNaN(item.transferDay) &&
		item.transferDay >= 0
	);

	const validOriginalData = (originalData || []).filter(item =>
		item.transferDay != null &&
		!isNaN(item.transferDay) &&
		item.transferDay >= 0
	);

	// Calculate average length of stay
	const calculateAverage = (items) => {
		if (!items || items.length === 0) return 0;
		const sum = items.reduce((acc, item) => acc + (item.transferDay || 0), 0);
		return sum / items.length;
	};

	const averageLOS = calculateAverage(validData);
	const originalAverageLOS = calculateAverage(validOriginalData);

	// Round to 1 decimal place
	const roundedAverage = Math.round(averageLOS * 10) / 10;
	const roundedOriginalAverage = Math.round(originalAverageLOS * 10) / 10;

	// Get patient IDs of those included in the calculation
	const patientIds = validData.map(item => isOtherDashboard ? item.refPatientId : item._id).filter(id => id != null);
	const otherDashboardIds = isOtherDashboard ? validData.map(item => item._id).filter(id => id != null) : undefined;

	// Ensure totalForPercentage is valid
	const percentageTotal = totalForPercentage || data.length;

	return [
		{
			id: ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA,
			label: ADMISSION_CARDS_LABELS.averageStayDaysData,
			_id: ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA,
			total: roundedAverage,
			originalTotal: roundedOriginalAverage,
			isTooltip: totalType !== TOTAL_TYPE.MAIN,
			color: "#606BDF", // You can change this color as needed
			patientIds,
			...(isOtherDashboard && { otherDashboardIds }),
			percentage: itemPercentage(validData.length, percentageTotal, "number"),
			isDefaultData: validData.length === 0,
			level: totalFilter?.level || 0,
			unit: "days", // Indicate the unit for display
			count: validData.length, // Number of patients included in calculation
		}
	];
}

export async function insuranceData(data, totalFilter) {
	//insuranceData Cal	
	let insuranceDBDataGroup = [];
	if (data && data.length > 0) {
		const insuranceDBData = _.groupBy(data, "insuranceId");
		const originalData = _.groupBy(totalFilter.originalData, "insuranceId");
		let percentageTotal = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : data?.length;

		if (insuranceDBData) {
			insuranceDBDataGroup = await filterListDataItems(insuranceDBData, "insurance", percentageTotal, {
				...totalFilter,
				originalData,
			});
		}
	}
	return insuranceDBDataGroup;
}

export async function returnsData(data, totalFilter) {
	if (!data?.length) return [];

	const { originalData = [], totalType = null, totalForPercentage, isOtherDashboard = false, level = 0, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter;
	const percentageTotal = totalForPercentage || data.length;

	const dintReturnResult = _.countBy(data, "wasReturned");

	const patientIdsByReturnStatus = _.groupBy(data, "wasReturned");

	const originalDataGroupBy = _.countBy(originalData, "wasReturned");

	// Intermediate totals for tooltip
	let level1Returned = 0;
	let level1DidntReturn = 0;
	let level2Returned = 0;
	let level2DidntReturn = 0;
	let level3Returned = 0;
	let level3DidntReturn = 0;
	let level4Returned = 0;
	let level4DidntReturn = 0;
	let level5Returned = 0;
	let level5DidntReturn = 0;

	if (currentLevel >= 2 && intermediateTotals?.level1 && Array.isArray(intermediateTotals.level1)) {
		const level1DataGroupBy = _.countBy(intermediateTotals.level1, "wasReturned");
		level1Returned = level1DataGroupBy?.["true"] || 0;
		level1DidntReturn = level1DataGroupBy?.["false"] || 0;
	}
	if (currentLevel >= 3 && intermediateTotals?.level2 && Array.isArray(intermediateTotals.level2)) {
		const level2DataGroupBy = _.countBy(intermediateTotals.level2, "wasReturned");
		level2Returned = level2DataGroupBy?.["true"] || 0;
		level2DidntReturn = level2DataGroupBy?.["false"] || 0;
	}
	if (currentLevel >= 4 && intermediateTotals?.level3 && Array.isArray(intermediateTotals.level3)) {
		const level3DataGroupBy = _.countBy(intermediateTotals.level3, "wasReturned");
		level3Returned = level3DataGroupBy?.["true"] || 0;
		level3DidntReturn = level3DataGroupBy?.["false"] || 0;
	}
	if (currentLevel >= 5 && intermediateTotals?.level4 && Array.isArray(intermediateTotals.level4)) {
		const level4DataGroupBy = _.countBy(intermediateTotals.level4, "wasReturned");
		level4Returned = level4DataGroupBy?.["true"] || 0;
		level4DidntReturn = level4DataGroupBy?.["false"] || 0;
	}
	if (currentLevel >= 6 && intermediateTotals?.level5 && Array.isArray(intermediateTotals.level5)) {
		const level5DataGroupBy = _.countBy(intermediateTotals.level5, "wasReturned");
		level5Returned = level5DataGroupBy?.["true"] || 0;
		level5DidntReturn = level5DataGroupBy?.["false"] || 0;
	}

	const createDetail = async (id, label, key) => ({
		id,
		label,
		_id: id,
		total: dintReturnResult[key] || 0,
		patientIds: (patientIdsByReturnStatus[key] || []).map(item => isOtherDashboard ? item.refPatientId : item._id),
		...isOtherDashboard && { otherDashboardIds: patientIdsByReturnStatus[key]?.map(item => item._id) },
		originalTotal: originalDataGroupBy[key] || 0,
		...(currentLevel >= 2 && key === "true" && { level1Total: level1Returned }),
		...(currentLevel >= 2 && key === "false" && { level1Total: level1DidntReturn }),
		...(currentLevel >= 3 && key === "true" && { level2Total: level2Returned }),
		...(currentLevel >= 3 && key === "false" && { level2Total: level2DidntReturn }),
		...(currentLevel >= 4 && key === "true" && { level3Total: level3Returned }),
		...(currentLevel >= 4 && key === "false" && { level3Total: level3DidntReturn }),
		...(currentLevel >= 5 && key === "true" && { level4Total: level4Returned }),
		...(currentLevel >= 5 && key === "false" && { level4Total: level4DidntReturn }),
		...(currentLevel >= 6 && key === "true" && { level5Total: level5Returned }),
		...(currentLevel >= 6 && key === "false" && { level5Total: level5DidntReturn }),
		...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
		...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
		...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
		...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
		...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
		isTooltip: totalType !== TOTAL_TYPE.MAIN,
		level,
		percentage: await itemPercentage(dintReturnResult[key] || 0, percentageTotal, "number"),
	});

	const returnDidNotDetails = await Promise.all([
		createDetail("Returned", "Returned", "true"),
		createDetail("Didn't Return", "Didn't Return", "false")
	]);

	return returnDidNotDetails;
}

async function processData(data, totalFilter, groupByKey, itemType) {
	if (!data?.length) return [];

	const groupedData = _.groupBy(data, groupByKey);
	const originalData = _.groupBy(totalFilter?.originalData || [], groupByKey);
	const percentageTotal = totalFilter?.totalForPercentage || data.length;
	const currentLevelDerived =
		totalFilter?.currentLevel ??
		(Array.isArray(totalFilter?.intermediateTotals?.level5)
			? 6
			: Array.isArray(totalFilter?.intermediateTotals?.level4)
				? 5
				: Array.isArray(totalFilter?.intermediateTotals?.level3)
					? 4
					: Array.isArray(totalFilter?.intermediateTotals?.level2)
						? 3
						: Array.isArray(totalFilter?.intermediateTotals?.level1)
							? 2
							: 0);

	return groupedData
		? await filterListDataItems(groupedData, itemType, percentageTotal, {
			...totalFilter,
			originalData,
			groupByKey,
			currentLevel: currentLevelDerived,
		})
		: [];
}

// Nurse data handler
export async function nurseData(data, totalFilter) {
	return await processData(data, totalFilter, "nurseId", "nurse");
}

export async function customHospitalData(data, totalFilter) {
	return await processData(data, totalFilter, "hospitalId", "hospital");
}

// Doctor data handler
export async function doctorData(data, totalFilter) {
	return await processData(data, totalFilter, "doctorId", "doctor");
}

// Floors data handler
export async function floorsData(data, totalFilter) {
	return await processData(data, totalFilter, "floorId", "unit");
}

// Helper function to get shift name from transferTime
const getShiftName = (time) => {
	if (time) {
		const format = 'HH:mm';

		const morningStart = moment("06:59", format);
		const morningEnd = moment("15:00", format);

		const eveningStart = moment("14:59", format);
		const eveningEnd = moment("23:00", format);

		const timeFormat = moment(time, 'h:mm a').format(format);

		const givenTime = moment(timeFormat, format);
		const givenTimeNextDay = moment(timeFormat, format);
		givenTimeNextDay.add(1, "days");

		if (givenTime.isAfter(morningStart) && givenTime.isBefore(morningEnd)) {
			return "Morning";
		}
		if (givenTime.isAfter(eveningStart) && givenTime.isBefore(eveningEnd)) {
			return "Evening";
		}
		return "Night";
	} else {
		return null;
	}
};

// Helper function to get shift name from transferTime using customQuestionOptions
export const getShiftNameFromCustomOptions = (time, customQuestionOptions) => {
	if (!time || !customQuestionOptions || customQuestionOptions.length === 0) {
		return null;
	}

	const format = 'HH:mm';
	const timeFormat = moment(time, 'h:mm a').format(format);
	const givenTime = moment(timeFormat, format);

	// Find which shift the time belongs to
	for (const shiftOption of customQuestionOptions) {
		const startTime = moment(shiftOption.startTime, format);
		const endTime = moment(shiftOption.endTime, format);

		// Check if time range spans midnight (e.g., 22:59 to 07:00)
		if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
			// Time range spans midnight
			// Match if: givenTime >= startTime (same day, e.g., 23:00 >= 22:59)
			// OR (givenTime <= endTime AND givenTime < startTime) (next day early hours, e.g., 06:00 <= 07:00 and 06:00 < 22:59)
			if (givenTime.isSameOrAfter(startTime) || (givenTime.isSameOrBefore(endTime) && givenTime.isBefore(startTime))) {
				return shiftOption.label;
			}
		} else {
			// Normal time range (doesn't span midnight)
			// Match if: startTime <= givenTime <= endTime
			if (givenTime.isSameOrAfter(startTime) && givenTime.isSameOrBefore(endTime)) {
				return shiftOption.label;
			}
		}
	}

	// If no shift matches, return the first shift as default (or null)
	return customQuestionOptions.length > 0 ? customQuestionOptions[0].label : null;
};

export async function shiftData(data, totalFilter) {
	if (!data || !Array.isArray(data)) {
		return [];
	}

	let totalData = data;

	const { originalData = [], totalType = null, isOtherDashboard = false, defaultCardEdited, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter;

	// Ensure originalData is an array
	const safeOriginalData = Array.isArray(originalData) ? originalData : [];

	let percentageTotal = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : data?.length;
	
	if (defaultCardEdited?.length > 0 && defaultCardEdited?.some(ele => ele?.accessor === "shiftData" && ele?.customQuestionOptions?.length > 0)) {
		const customQuestionOptions = defaultCardEdited?.find(ele => ele?.accessor === "shiftData")?.customQuestionOptions;	

		// Calculate shift from transferTime using customQuestionOptions
		const totalDataWithShift = totalData.map(item => ({
			...item,
			calculatedShift: getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) || (customQuestionOptions?.[0]?.label || "Night")
		}));

		const originalDataWithShift = safeOriginalData.map(item => ({
			...item,
			calculatedShift: getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) || (customQuestionOptions?.[0]?.label || "Night")
		}));

		const shiftDBData = _.countBy(totalDataWithShift, "calculatedShift");
		const originalDataGroupBy = _.countBy(originalDataWithShift, "calculatedShift");
		const patientIdsByShiftStatus = _.groupBy(totalDataWithShift, "calculatedShift");

		// Intermediate counts
		let level1Group = {};
		let level2Group = {};
		let level3Group = {};
		let level4Group = {};
		let level5Group = {};
		if (currentLevel >= 2 && Array.isArray(intermediateTotals?.level1)) {
			const level1WithShift = intermediateTotals.level1.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) ||
					customQuestionOptions?.[0]?.label ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level1Group = _.countBy(level1WithShift, "calculatedShift");
		}
		if (currentLevel >= 3 && Array.isArray(intermediateTotals?.level2)) {
			const level2WithShift = intermediateTotals.level2.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) ||
					customQuestionOptions?.[0]?.label ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level2Group = _.countBy(level2WithShift, "calculatedShift");
		}
		if (currentLevel >= 4 && Array.isArray(intermediateTotals?.level3)) {
			const level3WithShift = intermediateTotals.level3.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) ||
					customQuestionOptions?.[0]?.label ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level3Group = _.countBy(level3WithShift, "calculatedShift");
		}
		if (currentLevel >= 5 && Array.isArray(intermediateTotals?.level4)) {
			const level4WithShift = intermediateTotals.level4.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) ||
					customQuestionOptions?.[0]?.label ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level4Group = _.countBy(level4WithShift, "calculatedShift");
		}
		if (currentLevel >= 6 && Array.isArray(intermediateTotals?.level5)) {
			const level5WithShift = intermediateTotals.level5.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftNameFromCustomOptions(item.transferTime, customQuestionOptions) ||
					customQuestionOptions?.[0]?.label ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level5Group = _.countBy(level5WithShift, "calculatedShift");
		}

		// Build shift data array dynamically from customQuestionOptions
		const shiftDataDynamic = customQuestionOptions.map((shiftOption, index) => {
			const shiftLabel = shiftOption.label;
			const colorOptions = ["#636578", "#4879f5", "#636578", "#4879f5", "#636578"]; // Cycle through colors
			return {
				_id: shiftLabel,
				label: shiftLabel,
				value: shiftDBData?.[shiftLabel] || 0,
				total: shiftDBData?.[shiftLabel] || 0,
				originalTotal: originalDataGroupBy?.[shiftLabel] || 0,
				isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
				color: colorOptions[index % colorOptions.length],
				percentage: itemPercentage(shiftDBData?.[shiftLabel] || 0, percentageTotal, "number"),
				patientIds: (patientIdsByShiftStatus?.[shiftLabel]?.map(item => isOtherDashboard ? item.refPatientId : item._id) || []),
				level: totalFilter?.level || 0,
				...(currentLevel >= 2 && { level1Total: level1Group?.[shiftLabel] || 0 }),
				...(currentLevel >= 3 && { level2Total: level2Group?.[shiftLabel] || 0 }),
				...(currentLevel >= 4 && { level3Total: level3Group?.[shiftLabel] || 0 }),
				...(currentLevel >= 5 && { level4Total: level4Group?.[shiftLabel] || 0 }),
				...(currentLevel >= 6 && { level5Total: level5Group?.[shiftLabel] || 0 }),
				...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
				...isOtherDashboard && { otherDashboardIds: patientIdsByShiftStatus?.[shiftLabel]?.map(item => item._id) }
			};
		});

		return shiftDataDynamic;
	} else {
		// Calculate shift from transferTime instead of shiftName
		const totalDataWithShift = totalData.map(item => ({
			...item,
			calculatedShift: getShiftName(item.transferTime) || "Night" // Default to Night if no time
		}));

		const originalDataWithShift = safeOriginalData.map(item => ({
			...item,
			calculatedShift: getShiftName(item.transferTime) || "Night" // Default to Night if no time
		}));

		const shiftDBData = _.countBy(totalDataWithShift, "calculatedShift");
		const originalDataGroupBy = _.countBy(originalDataWithShift, "calculatedShift");
		const patientIdsByShiftStatus = _.groupBy(totalDataWithShift, "calculatedShift");

		// Intermediate counts
		let level1Group = {};
		let level2Group = {};
		let level3Group = {};
		let level4Group = {};
		let level5Group = {};
		if (currentLevel >= 2 && Array.isArray(intermediateTotals?.level1)) {
			const level1WithShift = intermediateTotals.level1.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftName(item.transferTime) ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level1Group = _.countBy(level1WithShift, "calculatedShift");
		}
		if (currentLevel >= 3 && Array.isArray(intermediateTotals?.level2)) {
			const level2WithShift = intermediateTotals.level2.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftName(item.transferTime) ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level2Group = _.countBy(level2WithShift, "calculatedShift");
		}
		if (currentLevel >= 4 && Array.isArray(intermediateTotals?.level3)) {
			const level3WithShift = intermediateTotals.level3.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftName(item.transferTime) ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level3Group = _.countBy(level3WithShift, "calculatedShift");
		}
		if (currentLevel >= 5 && Array.isArray(intermediateTotals?.level4)) {
			const level4WithShift = intermediateTotals.level4.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftName(item.transferTime) ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level4Group = _.countBy(level4WithShift, "calculatedShift");
		}
		if (currentLevel >= 6 && Array.isArray(intermediateTotals?.level5)) {
			const level5WithShift = intermediateTotals.level5.map(item => {
				const derived =
					item.calculatedShift ||
					item.shiftName ||
					getShiftName(item.transferTime) ||
					"Night";
				return { ...item, calculatedShift: derived };
			});
			level5Group = _.countBy(level5WithShift, "calculatedShift");
		}

		const shiftDataDefault = [
			{
				_id: "Morning",
				label: "Morning",
				value: shiftDBData?.Morning || 0,
				total: shiftDBData?.Morning || 0,
				originalTotal: originalDataGroupBy?.Morning || 0,
				isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
				color: "#636578",
				percentage: itemPercentage(shiftDBData?.Morning || 0, percentageTotal, "number"),
				patientIds: (patientIdsByShiftStatus?.Morning?.map(item => isOtherDashboard ? item.refPatientId : item._id) || []),
				level: totalFilter?.level || 0,
				...(currentLevel >= 2 && { level1Total: level1Group?.Morning || 0 }),
				...(currentLevel >= 3 && { level2Total: level2Group?.Morning || 0 }),
				...(currentLevel >= 4 && { level3Total: level3Group?.Morning || 0 }),
				...(currentLevel >= 5 && { level4Total: level4Group?.Morning || 0 }),
				...(currentLevel >= 6 && { level5Total: level5Group?.Morning || 0 }),
				...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
				...isOtherDashboard && { otherDashboardIds: patientIdsByShiftStatus?.Morning?.map(item => item._id) }
			},
			{
				_id: "Evening",
				label: "Evening",
				value: shiftDBData?.Evening || 0,
				total: shiftDBData?.Evening || 0,
				originalTotal: originalDataGroupBy?.Evening || 0,
				isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
				color: "#4879f5",
				percentage: itemPercentage(shiftDBData?.Evening || 0, percentageTotal, "number"),
				patientIds: (patientIdsByShiftStatus?.Evening?.map(item => isOtherDashboard ? item.refPatientId : item._id) || []),
				level: totalFilter?.level || 0,
				...(currentLevel >= 2 && { level1Total: level1Group?.Evening || 0 }),
				...(currentLevel >= 3 && { level2Total: level2Group?.Evening || 0 }),
				...(currentLevel >= 4 && { level3Total: level3Group?.Evening || 0 }),
				...(currentLevel >= 5 && { level4Total: level4Group?.Evening || 0 }),
				...(currentLevel >= 6 && { level5Total: level5Group?.Evening || 0 }),
				...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
				...isOtherDashboard && { otherDashboardIds: patientIdsByShiftStatus?.Evening?.map(item => item._id) }
			},
			{
				_id: "Night",
				label: "Night",
				value: shiftDBData?.Night || 0,
				total: shiftDBData?.Night || 0,
				originalTotal: originalDataGroupBy?.Night || 0,
				isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
				color: "#636578",
				percentage: itemPercentage(shiftDBData?.Night || 0, percentageTotal, "number"),
				patientIds: (patientIdsByShiftStatus?.Night?.map(item => isOtherDashboard ? item.refPatientId : item._id) || []),
				level: totalFilter?.level || 0,
				...(currentLevel >= 2 && { level1Total: level1Group?.Night || 0 }),
				...(currentLevel >= 3 && { level2Total: level2Group?.Night || 0 }),
				...(currentLevel >= 4 && { level3Total: level3Group?.Night || 0 }),
				...(currentLevel >= 5 && { level4Total: level4Group?.Night || 0 }),
				...(currentLevel >= 6 && { level5Total: level5Group?.Night || 0 }),
				...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
				...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
				...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
				...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
				...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
				...isOtherDashboard && { otherDashboardIds: patientIdsByShiftStatus?.Night?.map(item => item._id) }
			},
		];
		return shiftDataDefault;
	}
}

export async function daysData(data, totalFilter) {
	let totalData = data;
	const { originalData = [], totalType = null, isOtherDashboard = false, level = 0, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter;
	const originalDataGroupBy = _.countBy(originalData, "day");
	const dayDBData = _.countBy(totalData, "day");
	let percentageTotal = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : data?.length;
	const patientIdsByDayStatus = _.groupBy(totalData, "day");

	// Calculate level totals from intermediate snapshots
	let level1DataGroupBy = {};
	let level2DataGroupBy = {};
	let level3DataGroupBy = {};
	let level4DataGroupBy = {};
	let level5DataGroupBy = {};
	if (currentLevel >= 2 && intermediateTotals?.level1 && Array.isArray(intermediateTotals.level1)) {
		level1DataGroupBy = _.countBy(intermediateTotals.level1, "day");
	}
	if (currentLevel >= 3 && intermediateTotals?.level2 && Array.isArray(intermediateTotals.level2)) {
		level2DataGroupBy = _.countBy(intermediateTotals.level2, "day");
	}
	if (currentLevel >= 4 && intermediateTotals?.level3 && Array.isArray(intermediateTotals.level3)) {
		level3DataGroupBy = _.countBy(intermediateTotals.level3, "day");
	}
	if (currentLevel >= 5 && intermediateTotals?.level4 && Array.isArray(intermediateTotals.level4)) {
		level4DataGroupBy = _.countBy(intermediateTotals.level4, "day");
	}
	if (currentLevel >= 6 && intermediateTotals?.level5 && Array.isArray(intermediateTotals.level5)) {
		level5DataGroupBy = _.countBy(intermediateTotals.level5, "day");
	}

	const generateDayData = (dayKey) => ({
		_id: dayKey,
		label: dayKey,
		value: dayDBData?.[dayKey] || 0,
		originalTotal: originalDataGroupBy?.[dayKey] || 0,
		...(currentLevel >= 2 && { level1Total: level1DataGroupBy?.[dayKey] || 0 }),
		...(currentLevel >= 3 && { level2Total: level2DataGroupBy?.[dayKey] || 0 }),
		...(currentLevel >= 4 && { level3Total: level3DataGroupBy?.[dayKey] || 0 }),
		...(currentLevel >= 5 && { level4Total: level4DataGroupBy?.[dayKey] || 0 }),
		...(currentLevel >= 6 && { level5Total: level5DataGroupBy?.[dayKey] || 0 }),
		...(currentLevel >= 2 && cardTitles?.level1 && { level1CardTitle: cardTitles.level1 }),
		...(currentLevel >= 3 && cardTitles?.level2 && { level2CardTitle: cardTitles.level2 }),
		...(currentLevel >= 4 && cardTitles?.level3 && { level3CardTitle: cardTitles.level3 }),
		...(currentLevel >= 5 && cardTitles?.level4 && { level4CardTitle: cardTitles.level4 }),
		...(currentLevel >= 6 && cardTitles?.level5 && { level5CardTitle: cardTitles.level5 }),
		isTooltip: totalType !== TOTAL_TYPE.MAIN,
		color: "#FFECA6",
		percentage: itemPercentage(dayDBData?.[dayKey] || 0, percentageTotal, "number"),
		patientIds: patientIdsByDayStatus?.[dayKey]?.map(item => isOtherDashboard ? item.refPatientId : item._id) || [],
		level,
		...((isOtherDashboard) && { otherDashboardIds: patientIdsByDayStatus?.[dayKey]?.map(item => item._id) }),
	});

	const daysDataArr = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(generateDayData);
	return daysDataArr;
}

export async function dxData(data, totalFilter) {
	let totalData = data;
	const { originalData = [], totalType = null, totalForPercentage, isOtherDashboard = false, intermediateTotals = {}, currentLevel = 0, cardTitles = {} } = totalFilter;

	let dxDataArr = [];
	let patientData = [];
	let patientOriginalData = [];
	let dxOriginalDataArr = [];
	let patientDxIds = [];
	if (totalData && totalData.length > 0) {
		// eslint-disable-next-line array-callback-return
		totalData.filter((ele) => {
			if (ele.dx && ele.dx.length > 0) {
				dxDataArr = [...ele.dx, ...dxDataArr];
				patientData.push(ele);
				patientDxIds = [...ele.dxIds, ...patientDxIds];
			}
		});

		originalData.filter((ele) => {
			if (ele.dx && ele.dx.length > 0) {
				dxOriginalDataArr = [...ele.dx, ...dxOriginalDataArr];
				patientOriginalData.push(ele);
				// patientDxIds = [...ele.dxIds, ...patientDxIds];
			}
		});
	}
	let dxDataDB = [];
	let percentageTotal = totalForPercentage ? totalForPercentage : data?.length;

	// Build intermediate lookup for dxIds
	let level1Lookup = {};
	let level2Lookup = {};
	let level3Lookup = {};
	let level4Lookup = {};
	let level5Lookup = {};
	if (currentLevel >= 2 && Array.isArray(intermediateTotals?.level1)) {
		for (const p of intermediateTotals.level1) {
			(p?.dxIds || []).forEach((id) => {
				level1Lookup[id] = (level1Lookup[id] || 0) + 1;
			});
		}
	}
	if (currentLevel >= 3 && Array.isArray(intermediateTotals?.level2)) {
		for (const p of intermediateTotals.level2) {
			(p?.dxIds || []).forEach((id) => {
				level2Lookup[id] = (level2Lookup[id] || 0) + 1;
			});
		}
	}
	if (currentLevel >= 4 && Array.isArray(intermediateTotals?.level3)) {
		for (const p of intermediateTotals.level3) {
			(p?.dxIds || []).forEach((id) => {
				level3Lookup[id] = (level3Lookup[id] || 0) + 1;
			});
		}
	}
	if (currentLevel >= 5 && Array.isArray(intermediateTotals?.level4)) {
		for (const p of intermediateTotals.level4) {
			(p?.dxIds || []).forEach((id) => {
				level4Lookup[id] = (level4Lookup[id] || 0) + 1;
			});
		}
	}
	if (currentLevel >= 6 && Array.isArray(intermediateTotals?.level5)) {
		for (const p of intermediateTotals.level5) {
			(p?.dxIds || []).forEach((id) => {
				level5Lookup[id] = (level5Lookup[id] || 0) + 1;
			});
		}
	}

	if (dxDataArr.length > 0) {
		const dxDataGroup = _.groupBy(dxDataArr, "_id");
		const dxOriginalDataGroup = _.groupBy(dxOriginalDataArr, "_id");
		if (dxDataGroup) {
			for (const [key, value] of Object.entries(dxDataGroup)) {
				const valueArr = value[0];

				if (key && valueArr) {
					let object = Object();
					object._id = key;
					object.id = key;
					object.label = valueArr.label;
					object.name = valueArr.label;
					object.total = value.length;
					object.level = totalFilter?.level || 0;
					let original = dxOriginalDataGroup[key] ? dxOriginalDataGroup[key]?.length : 0;
					object.originalTotal = original;
					object.isTooltip = totalType && totalType === TOTAL_TYPE.MAIN ? false : true;
					object.chartData = {};
					object.percentage = itemPercentage(value.length || 0, percentageTotal || 0, "number");

					if (currentLevel >= 2) {
						object.level1Total = level1Lookup[key] || 0;
						if (cardTitles?.level1) {
							object.level1CardTitle = cardTitles.level1;
						}
					}
					if (currentLevel >= 3) {
						object.level2Total = level2Lookup[key] || 0;
						if (cardTitles?.level2) {
							object.level2CardTitle = cardTitles.level2;
						}
					}
					if (currentLevel >= 4) {
						object.level3Total = level3Lookup[key] || 0;
						if (cardTitles?.level3) {
							object.level3CardTitle = cardTitles.level3;
						}
					}
					if (currentLevel >= 5) {
						object.level4Total = level4Lookup[key] || 0;
						if (cardTitles?.level4) {
							object.level4CardTitle = cardTitles.level4;
						}
					}
					if (currentLevel >= 6) {
						object.level5Total = level5Lookup[key] || 0;
						if (cardTitles?.level5) {
							object.level5CardTitle = cardTitles.level5;
						}
					}

					// Get patientIds from `value`
					object.patientIds = patientData
						.filter(p => (p.dxIds || []).includes(key))
						.map(p => String(p._id));

					// Add otherDashboardIds if needed
					if (isOtherDashboard) {
						object.otherDashboardIds = patientData
							.filter(p => (p.dxIds || []).includes(key))
							.map(p => String(p._id));
					}

					dxDataDB.push(object);
				}
			}
		}
		dxDataDB = _.orderBy(dxDataDB, "total", "desc");
	}
	return dxDataDB;
}

export async function ninetyDaysDataList(data, patients, totalFilter) {
	const patientIds = patients?.length > 0 ? patients.map((item) => item.id) : [];
	let percentageTotal = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : patients?.length;

	const res = await updateListTotalValue(data, patientIds, "value", percentageTotal, totalFilter);
	return res;
}

export async function hospitalDataList(data, patients, totalFilter) {
	const { originalData = [], totalType = null } = totalFilter;

	const hospitalIdStrIds = patients?.length > 0 ? patients.map((item) => item.hospitalIdStr) : [];
	const patientIds = patients?.length > 0 ? patients.map((item) => item.id) : [];
	const patientIdsMain = originalData.length > 0 ? originalData.map((item) => item.id) : [];
	let patientData = _.filter(data, ({ _id }) => _.every([_.includes(hospitalIdStrIds, _id)]));

	let totalLength = totalFilter.totalForPercentage ? totalFilter.totalForPercentage : patients?.length;

	let newHospitalData = [];
	patientData.filter((ele) => {
		const intersection = matchedArray(patientIds, ele.ids);
		const intersectionMain = matchedArray(patientIdsMain, ele.ids);
		let patientDataDidNot = _.filter(patients, ({ id }) => _.every([_.includes(intersection, id)]));
		const wasReturnedCount = _.countBy(patientDataDidNot, "wasReturned");
		const wasAdmittedCount = _.countBy(patientDataDidNot, "wasAdmitted");
		const totalEle = _.find(ele.graphData, { id: "Total Transfer" });
		const chartArrData = [];
		chartArrData.push(
			{
				id: totalEle.id,
				data: [
					{
						x: totalEle?.id,
						y: intersection?.length || 0,
						color: "#15BDB2",
					},
				],
			},
			{
				id: "Total DC",
				data: [
					{
						x: "Total DC",
						y: wasAdmittedCount?.true || 0,
						color: "#076673",
					},
				],
			}
		);
		let original = intersectionMain.length || 0;
		let obj = {
			...ele,
			originalTotal: original,
			isTooltip: totalType && totalType === TOTAL_TYPE.MAIN ? false : true,
			totalTransfer: intersection.length || 0,
			total: intersection.length || 0,
			totalDidNotReturn: wasReturnedCount.false || 0,
			totalDCCount: wasAdmittedCount?.true || 0,
			graphData: chartArrData,
			ids: intersection || [],
			level: totalFilter?.level || 0,
			percentage: itemPercentage(intersection.length || 0, totalLength, "number"),
		};
		newHospitalData.push(obj);
		return Object.assign({}, ele, obj);
	});
	newHospitalData = _.orderBy(newHospitalData, "totalTransfer", "desc");
	return newHospitalData;
}

export function matchedArray(array1, array2) {
	return _.intersectionWith(array1, array2, _.isEqual);
}